// pages/OrganisationLimitDetail.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAdminPermissions } from "../context/AdminContext";
import limitsService from "../services/limitService";
import {
  Card,
  Row,
  Col,
  Statistic,
  Tag,
  Progress,
  Button,
  Form,
  InputNumber,
  Switch,
  Radio,
  Modal,
  message,
  Spin,
  Divider,
  Alert,
  Space,
  Descriptions,
} from "antd";
import {
  ArrowLeftOutlined,
  EditOutlined,
  ReloadOutlined,
  SaveOutlined,
  CloseOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";

const { confirm } = Modal;

const OrganisationLimitDetail = () => {
  const { organisationId } = useParams();
  const navigate = useNavigate();
  const { hasRouteAccess, canPerformAction } = useAdminPermissions();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [limit, setLimit] = useState(null);
  const [editing, setEditing] = useState(false);

  // Vérifier les permissions
  useEffect(() => {
    if (!hasRouteAccess("/organisations/:organisationId/limits")) {
      message.error("Accès refusé à cette ressource");
      navigate("/organisations/limits");
    }
  }, [hasRouteAccess, navigate]);

  // Charger les données
  useEffect(() => {
    fetchLimitData();
  }, [organisationId]);

  const fetchLimitData = async () => {
    setLoading(true);
    try {
      const response =
        await limitsService.getOrganisationLimits(organisationId);
      setLimit(response.data);

      // Initialiser le formulaire avec les valeurs actuelles
      form.setFieldsValue({
        daily_limit: response.data.daily.limit,
        monthly_limit: response.data.monthly.limit,
        is_active: response.data.is_active === 1,
      });
    } catch (error) {
      message.error("Erreur lors du chargement des données");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Formater les montants
  const formatAmount = (amount) => {
    return new Intl.NumberFormat("fr-FR").format(amount) + " FCFA";
  };

  // Obtenir la couleur de la progression
  const getProgressColor = (percentage) => {
    if (percentage >= 100) return "#ff4d4f";
    if (percentage >= 80) return "#faad14";
    return "#52c41a";
  };

  // Obtenir le statut de la progression
  const getProgressStatus = (percentage) => {
    if (percentage >= 100) return "exception";
    if (percentage >= 80) return "active";
    return "success";
  };

  // Sauvegarder les modifications
  const handleSave = async (values) => {
    setSaving(true);
    try {
      const payload = {
        daily_limit: values.daily_limit.toString(),
        monthly_limit: values.monthly_limit.toString(),
        is_active: values.is_active ? "1" : "0",
        reset_counters: values.reset_counters || "none",
      };

      await limitsService.updateOrganisationLimits(organisationId, payload);
      message.success("Limites mises à jour avec succès");
      setEditing(false);
      fetchLimitData();
    } catch (error) {
      message.error("Erreur lors de la mise à jour");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  // Réinitialiser les compteurs
  const handleResetCounters = (type) => {
    confirm({
      title: "Confirmer la réinitialisation",
      icon: <ExclamationCircleOutlined />,
      content: `Voulez-vous vraiment réinitialiser les compteurs ${
        type === "both"
          ? "journalier et mensuel"
          : type === "daily"
            ? "journalier"
            : "mensuel"
      } ?`,
      okText: "Oui, réinitialiser",
      okType: "danger",
      cancelText: "Annuler",
      onOk: async () => {
        try {
          await limitsService.resetCounters(organisationId, type);
          message.success("Compteurs réinitialisés avec succès");
          fetchLimitData();
        } catch (error) {
          message.error("Erreur lors de la réinitialisation");
          console.error(error);
        }
      },
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Spin size="large" />
      </div>
    );
  }

  if (!limit) {
    return (
      <div className="p-6">
        <Alert
          message="Erreur"
          description="Organisation non trouvée"
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* En-tête */}
      <div className="mb-6">
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/organisations/limits")}
          className="mb-4"
        >
          Retour
        </Button>

        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">{limit.name}</h1>
            <p className="text-gray-500">{limit.organisation_id}</p>
          </div>

          <Space>
            <Tag
              color={limit.verification_level === "level_2" ? "green" : "blue"}
              className="text-lg px-4 py-2"
            >
              {limit.verification_level === "level_2" ? "LEVEL 2" : "LEVEL 1"}
            </Tag>
            <Tag
              color={limit.is_active === 1 ? "success" : "default"}
              className="text-lg px-4 py-2"
            >
              {limit.is_active === 1 ? "Actif" : "Inactif"}
            </Tag>
          </Space>
        </div>
      </div>

      {/* Alertes */}
      {(limit.daily.percentage >= 100 || limit.monthly.percentage >= 100) && (
        <Alert
          message="Limite dépassée"
          description="Cette organisation a dépassé ses limites de transaction"
          type="error"
          showIcon
          closable
          className="mb-4"
        />
      )}

      {(limit.daily.percentage >= 80 || limit.monthly.percentage >= 80) &&
        limit.daily.percentage < 100 &&
        limit.monthly.percentage < 100 && (
          <Alert
            message="Attention"
            description="Cette organisation approche de ses limites"
            type="warning"
            showIcon
            closable
            className="mb-4"
          />
        )}

      <Row gutter={[16, 16]}>
        {/* Colonne Gauche - Statistiques */}
        <Col xs={24} lg={12}>
          <Card title="Utilisation Journalière" className="mb-4">
            <Statistic
              title="Utilisé"
              value={limit.daily.used}
              suffix={` / ${formatAmount(limit.daily.limit)}`}
              valueStyle={{ color: getProgressColor(limit.daily.percentage) }}
            />
            <Progress
              percent={Math.round(limit.daily.percentage * 100) / 100}
              strokeColor={getProgressColor(limit.daily.percentage)}
              status={getProgressStatus(limit.daily.percentage)}
              className="mt-4"
            />
            <Descriptions size="small" className="mt-4">
              <Descriptions.Item label="Disponible">
                {formatAmount(limit.daily.available)}
              </Descriptions.Item>
            </Descriptions>
            <Descriptions size="small">
              <Descriptions.Item label="Réinitialisation">
                {new Date(limit.daily.reset_at).toLocaleString("fr-FR")}
              </Descriptions.Item>
            </Descriptions>

            <Divider />

            <Button
              type="primary"
              danger
              icon={<ReloadOutlined />}
              onClick={() => handleResetCounters("daily")}
              block
            >
              Réinitialiser le compteur journalier
            </Button>
          </Card>

          <Card title="Utilisation Mensuelle">
            <Statistic
              title="Utilisé"
              value={limit.monthly.used}
              suffix={` / ${formatAmount(limit.monthly.limit)}`}
              valueStyle={{ color: getProgressColor(limit.monthly.percentage) }}
            />
            <Progress
              percent={Math.round(limit.monthly.percentage * 100) / 100}
              strokeColor={getProgressColor(limit.monthly.percentage)}
              status={getProgressStatus(limit.monthly.percentage)}
              className="mt-4"
            />
            <Descriptions size="small" className="mt-4">
              <Descriptions.Item label="Disponible">
                {formatAmount(limit.monthly.available)}
              </Descriptions.Item>
            </Descriptions>
            <Descriptions size="small">
              <Descriptions.Item label="Réinitialisation">
                {new Date(limit.monthly.reset_at).toLocaleString("fr-FR")}
              </Descriptions.Item>
            </Descriptions>

            <Divider />

            <Space direction="vertical" style={{ width: "100%" }}>
              <Button
                type="primary"
                danger
                icon={<ReloadOutlined />}
                onClick={() => handleResetCounters("monthly")}
                block
              >
                Réinitialiser le compteur mensuel
              </Button>

              <Button
                danger
                icon={<ReloadOutlined />}
                onClick={() => handleResetCounters("both")}
                block
              >
                Réinitialiser les deux compteurs
              </Button>
            </Space>
          </Card>
        </Col>

        {/* Colonne Droite - Formulaire de modification */}
        <Col xs={24} lg={12}>
          <Card
            title="Configuration des limites"
            extra={
              !editing && (
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={() => setEditing(true)}
                >
                  Modifier
                </Button>
              )
            }
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSave}
              disabled={!editing}
            >
              <Form.Item
                label="Limite Journalière (FCFA)"
                name="daily_limit"
                rules={[
                  { required: true, message: "Requis" },
                  { type: "number", min: 0, message: "Doit être positif" },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")
                  }
                  parser={(value) => value.replace(/\s?/g, "")}
                  placeholder="Ex: 1000000"
                />
              </Form.Item>

              <Form.Item
                label="Limite Mensuelle (FCFA)"
                name="monthly_limit"
                rules={[
                  { required: true, message: "Requis" },
                  { type: "number", min: 0, message: "Doit être positif" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("daily_limit") <= value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error(
                          "La limite mensuelle doit être ≥ limite journalière",
                        ),
                      );
                    },
                  }),
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")
                  }
                  parser={(value) => value.replace(/\s?/g, "")}
                  placeholder="Ex: 10000000"
                />
              </Form.Item>

              <Form.Item
                label="Statut des limites"
                name="is_active"
                valuePropName="checked"
              >
                <Switch checkedChildren="Actif" unCheckedChildren="Inactif" />
              </Form.Item>

              {editing && (
                <Form.Item
                  label="Réinitialiser les compteurs ?"
                  name="reset_counters"
                  initialValue="none"
                >
                  <Radio.Group>
                    <Radio value="none">Ne pas réinitialiser</Radio>
                    <Radio value="daily">Compteur journalier</Radio>
                    <Radio value="monthly">Compteur mensuel</Radio>
                    <Radio value="both">Les deux compteurs</Radio>
                  </Radio.Group>
                </Form.Item>
              )}

              {editing && (
                <Form.Item>
                  <Space>
                    <Button
                      type="primary"
                      htmlType="submit"
                      icon={<SaveOutlined />}
                      loading={saving}
                    >
                      Enregistrer
                    </Button>
                    <Button
                      icon={<CloseOutlined />}
                      onClick={() => {
                        setEditing(false);
                        form.resetFields();
                      }}
                    >
                      Annuler
                    </Button>
                  </Space>
                </Form.Item>
              )}
            </Form>

            <Divider />

            <Descriptions title="Informations" size="small" column={1}>
              <Descriptions.Item label="Niveau de vérification">
                {limit.verification_level}
              </Descriptions.Item>
              <Descriptions.Item label="Date de création">
                {new Date(limit.created_at).toLocaleString("fr-FR")}
              </Descriptions.Item>
              <Descriptions.Item label="Dernière modification">
                {new Date(limit.updated_at).toLocaleString("fr-FR")}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default OrganisationLimitDetail;
